import { useCallback, useEffect, useRef, useState } from 'react';

interface UseSearchBarStateOptions {
  value?: string;
  onChange?: (value: string) => void;
  onSearch?: (value: string) => void;
  onClear?: () => void;
  debounceMs?: number;
  searchEmptyImmediately?: boolean;
  autoFocus?: boolean;
}

export interface UseSearchBarStateReturn {
  // State
  internalValue: string;
  isFocused: boolean;

  // Refs
  inputRef: React.RefObject<HTMLInputElement>;

  // Handlers
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputFocus: () => void;
  handleInputBlur: () => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  handleClear: () => void;
}

export function useSearchBarState({
  value = '',
  onChange,
  onSearch,
  onClear,
  debounceMs = 300,
  searchEmptyImmediately = true,
  autoFocus = false
}: UseSearchBarStateOptions = {}): UseSearchBarStateReturn {
  const [internalValue, setInternalValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSearchValueRef = useRef<string>(value);

  const clearPendingSearch = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
  }, []);

  const executeSearch = useCallback(
    (searchValue: string, options?: { force?: boolean }) => {
      if (!options?.force && searchValue === lastSearchValueRef.current) {
        return;
      }
      lastSearchValueRef.current = searchValue;
      onSearch?.(searchValue);
    },
    [onSearch]
  );

  // Sincronizar com valor externo
  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  // Auto focus
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  // Função de busca com debounce
  const debouncedSearch = useCallback(
    (searchValue: string) => {
      if (debounceMs <= 0) {
        executeSearch(searchValue);
        return;
      }

      clearPendingSearch();
      debounceTimeoutRef.current = setTimeout(() => {
        executeSearch(searchValue);
      }, debounceMs);
    },
    [clearPendingSearch, executeSearch, debounceMs]
  );

  // Handlers
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInternalValue(newValue);
      onChange?.(newValue);

      if (newValue === '' && searchEmptyImmediately) {
        clearPendingSearch();
        executeSearch('', { force: internalValue !== '' });
        return;
      }

      debouncedSearch(newValue);
    },
    [
      onChange,
      clearPendingSearch,
      executeSearch,
      debouncedSearch,
      internalValue,
      searchEmptyImmediately
    ]
  );

  const handleInputFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleInputBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        clearPendingSearch();
        executeSearch(internalValue);
      }
    },
    [clearPendingSearch, executeSearch, internalValue]
  );

  const handleClear = useCallback(() => {
    setInternalValue('');
    onChange?.('');
    onClear?.();
    clearPendingSearch();
    executeSearch('', { force: true });

    // Focar no input após limpar
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [clearPendingSearch, executeSearch, onChange, onClear]);

  // Cleanup
  useEffect(() => {
    return () => {
      clearPendingSearch();
    };
  }, [clearPendingSearch]);

  return {
    internalValue,
    isFocused,
    inputRef,
    handleInputChange,
    handleInputFocus,
    handleInputBlur,
    handleKeyDown,
    handleClear
  };
}
